import { FC, useCallback, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import { ButtonGroup, OutlinedButton } from "dash-styled"
import type { KeyboardShortcut, PrelabelResponse, UpdateReturn } from "resha"
import { SchemaForm } from "universal-schema/lib"
import { ReactComponent as ExpandIcon } from "icons/lib/expand.svg"
import { ReactComponent as SwitchIcon } from "icons/lib/switch-on.svg"

import {
    APIJobModel,
    JobVersionItem
} from "../../../../../../Models/API/JobModel"
import { PostProcessedOutput } from "../../../../../../Models/DataProgram/TemplateMetadata"
import { APIAppInstanceModel } from "../../../../../../Models/API/AppInstanceModel"
import { TaskListQueryParams } from "../../../../../../Models/API/TaskModel"
import { ProjectRole } from "../../../../../../Models/Client/ProjectRole"
import {
    startTaskListPoll,
    stopTaskListPoll
} from "../../../../../../Actions/API/Job/TaskActions"
import {
    startPollJobResponse,
    stopPollJobResponse
} from "../../../../../../Actions/API/Job/JobActions"
import { useValues } from "../../../../../../Hooks/useValues"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import {
    jsonAndFormattedOutputFormats,
    possibleOutputFormats
} from "../Common/constants"
import { TabButton } from "../../../../../Common/Button/TabButton"
import { routePaths } from "../../../../../../Routes"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { JobDetailInputOutput } from "./InputOutput"

const OnSwitchIcon: FC<{ className: string }> = ({ className }) => (
    <SwitchIcon className={`${className ?? ""} text-grape-500`} />
)
const OffSwitchIcon: FC<{ className: string }> = ({ className }) => (
    <SwitchIcon
        className={`${className ?? ""} transform rotate-180 text-smoke-800`}
    />
)

export type JobDataViewTabs = "input" | "output"

export interface JobDataDetailViewProps {
    orgUsername: string
    appID: string
    job: APIJobModel
    instance: APIAppInstanceModel
    inputSchema: SchemaForm
    outputSchema: SchemaForm
    versionsList: JobVersionItem[]
    selectedVersion: number
    postProcessedData: PostProcessedOutput
    postProcessTitle?: string
    role: ProjectRole[]
    prelabels: PrelabelResponse
    showOldVersion: boolean
    isReviewView: boolean
    dataTab?: JobDataViewTabs
    onChangeDataTab: (dataTab: JobDataViewTabs) => void
    onClickReviewView: (reviewView: boolean) => void
    onEnqueue?: () => Promise<void>
    onRelabel?: () => Promise<void>
    handleCancel?: () => Promise<void>
    urlSigner: (url: string) => Promise<string>
    onItemChange?: (item: UpdateReturn<unknown> | undefined) => void
    onReviewChange?: () => void
    onEditAttempt?: () => void
    onShortcutList?: (shortcutList: {
        [key: string]: [string, KeyboardShortcut][]
    }) => void
}

export const JobDataDetailView = ({
    orgUsername,
    appID,
    job,
    instance,
    inputSchema,
    outputSchema,
    versionsList,
    selectedVersion,
    role,
    prelabels,
    showOldVersion,
    isReviewView,
    dataTab,
    onChangeDataTab,
    onEnqueue,
    onRelabel,
    handleCancel,
    urlSigner,
    onClickReviewView,
    postProcessedData,
    postProcessTitle,
    onItemChange,
    onReviewChange,
    onEditAttempt,
    onShortcutList
}: JobDataDetailViewProps) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const scrollRef = useRef<HTMLDivElement>(null)

    const [showPostprocessed, , , toggleShowPostprocessed] = useBoolean(false)

    const [outputFormat, ...setOutputFormatType] = useValues<
        typeof possibleOutputFormats[number]
    >(jsonAndFormattedOutputFormats)

    const currentJob = versionsList[selectedVersion]

    useEffect(() => {
        if (showPostprocessed && !currentJob.responseData) {
            dispatch(startPollJobResponse(appID, job.id))
        }
        return () => {
            dispatch(stopPollJobResponse(job.id))
        }
    }, [dispatch, appID, job.id, showPostprocessed, currentJob.responseData])

    const handleRequestTaskDetail = useCallback(
        (taskId: number) => {
            window.open(
                routePaths.taskDetails(
                    orgUsername,
                    appID,
                    job.id,
                    String(selectedVersion),
                    String(taskId)
                ),
                "_blank",
                "noopener,noreferrer"
            )
        },
        [appID, job.id, orgUsername, selectedVersion]
    )

    useEffect(() => {
        dispatch(
            startTaskListPoll(currentJob.versionJobId, TaskListQueryParams)
        )

        return () => {
            dispatch(stopTaskListPoll())
        }
    }, [currentJob.versionJobId, dispatch])

    const handleOpenInput = useCallback(
        () => onChangeDataTab("input"),
        [onChangeDataTab]
    )
    const handleOpenOuput = useCallback(
        () => onChangeDataTab("output"),
        [onChangeDataTab]
    )

    const handleOpenReview = useCallback(
        () => onClickReviewView(true),
        [onClickReviewView]
    )

    return (
        <>
            {!isReviewView && (
                <>
                    <ButtonGroup equalWidth={false}>
                        <TabButton
                            onClick={handleOpenInput}
                            active={dataTab === "input" || !dataTab}
                        >
                            Input
                        </TabButton>
                        <TabButton
                            onClick={handleOpenOuput}
                            active={dataTab === "output"}
                        >
                            Output
                        </TabButton>
                    </ButtonGroup>
                    <div className="flex justify-between flex-shrink-0 m-3 mb-0">
                        {postProcessedData === "available" &&
                            instance.settings?.postProcessing &&
                            dataTab === "output" && (
                                <div className="flex-grow">
                                    <OutlinedButton
                                        IconEnd={
                                            showPostprocessed
                                                ? OnSwitchIcon
                                                : OffSwitchIcon
                                        }
                                        label="Show Postprocessing"
                                        onClick={toggleShowPostprocessed}
                                    />
                                </div>
                            )}
                        <ButtonGroup equalWidth={false}>
                            <TabButton
                                onClick={setOutputFormatType[0]}
                                active={outputFormat === "formatted"}
                            >
                                Formatted
                            </TabButton>
                            <TabButton
                                onClick={setOutputFormatType[1]}
                                active={outputFormat === "json"}
                            >
                                JSON
                            </TabButton>
                        </ButtonGroup>
                        {dataTab === "output" && (
                            <OutlinedButton
                                className="ml-2"
                                Icon={ExpandIcon}
                                label="Expand"
                                onClick={handleOpenReview}
                            />
                        )}
                    </div>
                </>
            )}
            <div
                ref={scrollRef}
                className={`flex flex-col flex-grow overflow-auto ${
                    isReviewView ? "" : "m-3"
                }`}
            >
                <JobDetailInputOutput
                    isReviewView={isReviewView}
                    mode={isReviewView ? "output" : dataTab || "input"}
                    key={
                        job.id /* force re-render on job id change, so that internal state of TaskItem components get cleared */
                    }
                    job={job}
                    inputSchema={inputSchema}
                    outputSchema={outputSchema}
                    instance={instance}
                    selectedVersion={currentJob}
                    outputFormatType={
                        dataTab === "output" &&
                        outputFormat === "formatted" &&
                        showPostprocessed
                            ? "processed"
                            : outputFormat
                    }
                    postProcessedData={postProcessedData}
                    postProcessTitle={postProcessTitle}
                    role={role}
                    showOldVersion={showOldVersion}
                    prelabels={prelabels}
                    onEnqueue={onEnqueue}
                    onRelabel={onRelabel}
                    handleCancel={handleCancel}
                    urlSigner={urlSigner}
                    onRequestTaskDetail={handleRequestTaskDetail}
                    onItemChange={onItemChange}
                    onReviewChange={onReviewChange}
                    onEditAttempt={onEditAttempt}
                    onShortcutList={onShortcutList}
                />
            </div>
        </>
    )
}
